import checkoutI18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import ar from './locales/ar.json'
import de from './locales/de.json'
import en from './locales/en.json'
import es from './locales/es.json'
import fr from './locales/fr.json'
import id from './locales/id.json'
import ja from './locales/ja.json'
import ko from './locales/ko.json'
import pt from './locales/pt.json'
import ru from './locales/ru.json'
import th from './locales/th.json'
import vi from './locales/vi.json'
import zhTW from './locales/zh-TW.json'
import zh from './locales/zh.json'

/**
 * Locale Configuration
 *
 * Add a new language: just add one entry to this array
 */
export const LOCALES = [
  { code: 'en', name: 'English', isoCode: 'en-US', translation: en },
  { code: 'de', name: 'Deutsch', isoCode: 'de-DE', translation: de },
  { code: 'es', name: 'Español', isoCode: 'es-ES', translation: es },
  { code: 'fr', name: 'Français', isoCode: 'fr-FR', translation: fr },
  { code: 'id', name: 'Bahasa Indonesia', isoCode: 'id-ID', translation: id },
  { code: 'ja', name: '日本語', isoCode: 'ja-JP', translation: ja },
  { code: 'ko', name: '한국어', isoCode: 'ko-KR', translation: ko },
  { code: 'pt', name: 'Português', isoCode: 'pt-BR', translation: pt },
  { code: 'ru', name: 'Русский', isoCode: 'ru-RU', translation: ru },
  { code: 'ar', name: 'العربية', isoCode: 'ar-SA', translation: ar },
  { code: 'th', name: 'ไทย', isoCode: 'th-TH', translation: th },
  { code: 'vi', name: 'Tiếng Việt', isoCode: 'vi-VN', translation: vi },
  { code: 'zh', name: '简体中文', isoCode: 'zh-CN', translation: zh },
  { code: 'zh-TW', name: '繁體中文', isoCode: 'zh-TW', translation: zhTW },
] as const

// Derived types
export type Locale = (typeof LOCALES)[number]['code']
export type IsoCode = (typeof LOCALES)[number]['isoCode']

// Derived constants
export const SUPPORTED_LOCALES = LOCALES.map((l) => l.code)
export const LOCALE_NAMES = Object.fromEntries(LOCALES.map((l) => [l.code, l.name])) as Record<
  Locale,
  string
>

// Build i18next resources
const resources = Object.fromEntries(LOCALES.map((l) => [l.code, { translation: l.translation }]))

const isoToLocale = Object.fromEntries(
  LOCALES.flatMap((l) => [
    [l.isoCode.toLowerCase(), l.code],
    [l.code.toLowerCase(), l.code],
  ])
) as Record<string, Locale>

function normalizeLocaleCode(code: string | null | undefined): Locale | null {
  const normalized = code?.trim().replace('_', '-').toLowerCase()
  if (!normalized) {
    return null
  }

  const language = normalized.split('-')[0]
  return isoToLocale[normalized] || (language ? isoToLocale[language] : null) || null
}

export function getLocaleFormatCode(code: string | null | undefined): IsoCode {
  const locale = normalizeLocaleCode(code) ?? 'en'
  return LOCALES.find((item) => item.code === locale)?.isoCode ?? 'en-US'
}

// Custom language detector
const queryStringDetector = {
  name: 'customQuerystring',
  lookup(): string | undefined {
    if (typeof window === 'undefined') return undefined
    const params = new URLSearchParams(window.location.search)
    const lang = params.get('lang')
    if (!lang) return undefined

    const normalized = lang.trim().toLowerCase()
    return isoToLocale[normalized] || normalized.split('-')[0]
  },
}

// Initialize i18next
const languageDetector = new LanguageDetector()
languageDetector.addDetector(queryStringDetector)

void checkoutI18n['use'](languageDetector)
  ['use'](initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES,
    interpolation: { escapeValue: false },
    detection: {
      order: ['customQuerystring', 'querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'checkout-locale',
      caches: ['localStorage'],
    },
  })

export default checkoutI18n

// Helper functions
export const getLocaleByCode = (code: string) => LOCALES.find((l) => l.code === code)
export const getLocaleByIsoCode = (isoCode: string) =>
  LOCALES.find((l) => l.isoCode.toLowerCase() === isoCode.toLowerCase())
export const getLocaleDisplayName = (code: Locale) => getLocaleByCode(code)?.name ?? code
export const getAvailableLocales = () => LOCALES.map(({ code, name }) => ({ code, name }))
